import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', tealDark: '#0087A8',
  bg: '#F0F7FA', success: '#10B981', warning: '#F59E0B',
  error: '#EF4444', dark: '#1F2937', grey: '#6B7280',
  border: '#E5E7EB', white: '#FFFFFF', lightTeal: '#E0F7FA',
};

const METHOD_LABELS = { bank_transfer: 'Bank Transfer', paypal: 'PayPal', wise: 'Wise' };
const METHOD_COLORS = { bank_transfer: '#3B82F6', paypal: '#003087', wise: '#37517e' };

export default function AdminPayouts() {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [payRef, setPayRef] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [markingId, setMarkingId] = useState(null);
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('pending'); // 'pending' | 'all'

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/admin/payouts');
      setTutors(res.data.tutors || []);
    } catch (e) {
      setError('Failed to load payout data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleMarkPaid = async (tutorId) => {
    setMarkingId(tutorId);
    try {
      await api.post(`/api/admin/payouts/${tutorId}/mark-paid`, {
        payment_ref: payRef,
        notes: payNotes,
      });
      setSuccess('Payout marked as paid and tutor notified!');
      setConfirmId(null);
      setPayRef('');
      setPayNotes('');
      await load();
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to mark payout as paid.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setMarkingId(null);
    }
  };

  const displayed = filter === 'pending'
    ? tutors.filter(t => t.pending_earnings_count > 0)
    : tutors;

  const totalPending = tutors.reduce((s, t) => s + (t.pending_net_usd || 0), 0);
  const pendingCount = tutors.filter(t => t.pending_earnings_count > 0).length;

  const nextPayoutDate = (() => {
    const now = new Date();
    const candidate = new Date(now.getFullYear(), now.getMonth(), 5);
    if (now.getDate() >= 5) {
      candidate.setMonth(candidate.getMonth() + 1);
    }
    return candidate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  })();

  return (
    <Layout role="tenant_admin">
      <div style={{ padding: '32px 24px', maxWidth: 1100, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: C.dark, margin: 0 }}>Tutor Payouts</h1>
            <p style={{ color: C.grey, margin: '4px 0 0', fontSize: 14 }}>
              Manage monthly payments to tutors · Next payout date: <strong>{nextPayoutDate}</strong>
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setFilter('pending')}
              style={{
                padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 14,
                border: `2px solid ${filter === 'pending' ? C.teal : C.border}`,
                background: filter === 'pending' ? C.teal : C.white,
                color: filter === 'pending' ? C.white : C.dark,
                cursor: 'pointer',
              }}
            >
              Pending ({pendingCount})
            </button>
            <button
              onClick={() => setFilter('all')}
              style={{
                padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 14,
                border: `2px solid ${filter === 'all' ? C.teal : C.border}`,
                background: filter === 'all' ? C.teal : C.white,
                color: filter === 'all' ? C.white : C.dark,
                cursor: 'pointer',
              }}
            >
              All Tutors
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 28 }}>
          {[
            { label: 'Tutors Awaiting Payment', value: pendingCount, color: C.warning },
            { label: 'Total Pending (Net)', value: `$${totalPending.toFixed(2)}`, color: C.teal },
            { label: 'Next Payout Date', value: nextPayoutDate.split(' ').slice(0, 2).join(' '), color: C.success },
          ].map(card => (
            <div key={card.label} style={{
              background: C.white, borderRadius: 12, padding: '20px',
              border: `1px solid ${C.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}>
              <div style={{ fontSize: 12, color: C.grey, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{card.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: card.color }}>{card.value}</div>
            </div>
          ))}
        </div>

        {/* Alerts */}
        {success && (
          <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', color: '#065F46', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontWeight: 500 }}>
            ✓ {success}
          </div>
        )}
        {error && (
          <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontWeight: 500 }}>
            ✗ {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: C.grey }}>Loading payout data…</div>
        ) : displayed.length === 0 ? (
          <div style={{ background: C.white, borderRadius: 16, padding: '60px 24px', textAlign: 'center', border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <h3 style={{ color: C.dark, fontWeight: 700, margin: '0 0 8px' }}>All Caught Up!</h3>
            <p style={{ color: C.grey, margin: 0 }}>No pending payouts at the moment.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {displayed.map(tutor => (
              <TutorPayoutCard
                key={tutor.tutor_id}
                tutor={tutor}
                expanded={expandedId === tutor.tutor_id}
                onToggle={() => setExpandedId(expandedId === tutor.tutor_id ? null : tutor.tutor_id)}
                confirming={confirmId === tutor.tutor_id}
                onConfirmOpen={() => { setConfirmId(tutor.tutor_id); setPayRef(''); setPayNotes(''); }}
                onConfirmClose={() => setConfirmId(null)}
                payRef={payRef}
                setPayRef={setPayRef}
                payNotes={payNotes}
                setPayNotes={setPayNotes}
                onMarkPaid={() => handleMarkPaid(tutor.tutor_id)}
                marking={markingId === tutor.tutor_id}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function TutorPayoutCard({ tutor, expanded, onToggle, confirming, onConfirmOpen, onConfirmClose, payRef, setPayRef, payNotes, setPayNotes, onMarkPaid, marking }) {
  const hasPending = tutor.pending_earnings_count > 0;
  const hasPaymentInfo = tutor.has_payment_info;
  const method = tutor.payment_method || 'bank_transfer';

  return (
    <div style={{
      background: C.white, borderRadius: 16, border: `1px solid ${hasPending ? C.border : '#E5E7EB'}`,
      boxShadow: hasPending ? '0 2px 8px rgba(0,0,0,0.07)' : '0 1px 3px rgba(0,0,0,0.04)',
      overflow: 'hidden',
      opacity: hasPending ? 1 : 0.75,
    }}>
      {/* Card header */}
      <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        {/* Avatar */}
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: `linear-gradient(135deg, ${C.teal}, ${C.tealDark})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: C.white, fontWeight: 700, fontSize: 18, flexShrink: 0,
        }}>
          {(tutor.first_name?.[0] || '?').toUpperCase()}
        </div>

        {/* Name & info */}
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 16, color: C.dark }}>
              {tutor.first_name} {tutor.last_name}
            </span>
            {tutor.tutor_id_number && (
              <span style={{ fontSize: 12, color: C.grey, background: '#F3F4F6', padding: '2px 8px', borderRadius: 20 }}>
                {tutor.tutor_id_number}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: C.grey, marginTop: 2 }}>{tutor.email}</div>
        </div>

        {/* Amounts */}
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: C.grey, fontWeight: 600, textTransform: 'uppercase' }}>Pending Net</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: hasPending ? C.teal : C.grey }}>
              ${(tutor.pending_net_usd || 0).toFixed(2)}
            </div>
            <div style={{ fontSize: 12, color: C.grey }}>{tutor.pending_earnings_count} lesson{tutor.pending_earnings_count !== 1 ? 's' : ''}</div>
          </div>
        </div>

        {/* Status badges + actions */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {hasPaymentInfo ? (
            <span style={{
              fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20,
              background: '#D1FAE5', color: '#065F46',
            }}>
              {METHOD_LABELS[method] || method}
            </span>
          ) : (
            <span style={{
              fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20,
              background: '#FEF3C7', color: '#92400E',
            }}>
              No Payment Info
            </span>
          )}

          {hasPending && (
            <button
              onClick={onConfirmOpen}
              disabled={!hasPaymentInfo || marking}
              title={!hasPaymentInfo ? 'Tutor has not added payment info yet' : ''}
              style={{
                padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13,
                background: (!hasPaymentInfo || marking) ? '#E5E7EB' : C.teal,
                color: (!hasPaymentInfo || marking) ? C.grey : C.white,
                border: 'none', cursor: (!hasPaymentInfo || marking) ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
              }}
            >
              {marking ? 'Processing…' : 'Mark as Paid'}
            </button>
          )}

          <button
            onClick={onToggle}
            style={{
              padding: '8px 12px', borderRadius: 8, fontWeight: 600, fontSize: 13,
              background: '#F3F4F6', color: C.dark, border: 'none', cursor: 'pointer',
            }}
          >
            {expanded ? '▲ Hide' : '▼ Details'}
          </button>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '20px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 24 }}>
          {/* Payment info */}
          <div>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: C.dark }}>💳 Payment Details</h4>
            {!hasPaymentInfo ? (
              <p style={{ color: C.grey, fontSize: 13, margin: 0 }}>Tutor has not entered payment information yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <InfoRow label="Method" value={METHOD_LABELS[method] || method} />
                {method === 'bank_transfer' && (
                  <>
                    {tutor.account_holder && <InfoRow label="Account Holder" value={tutor.account_holder} />}
                    {tutor.bank_name && <InfoRow label="Bank Name" value={tutor.bank_name} />}
                    {tutor.account_number && <InfoRow label="Account / IBAN" value={tutor.account_number} mono />}
                    {tutor.sort_code && <InfoRow label="Sort Code" value={tutor.sort_code} mono />}
                    {tutor.swift_bic && <InfoRow label="SWIFT / BIC" value={tutor.swift_bic} mono />}
                  </>
                )}
                {method === 'paypal' && tutor.paypal_email && (
                  <InfoRow label="PayPal Email" value={tutor.paypal_email} />
                )}
                {method === 'wise' && tutor.wise_email && (
                  <InfoRow label="Wise Email" value={tutor.wise_email} />
                )}
                {tutor.payment_notes && <InfoRow label="Notes" value={tutor.payment_notes} />}
              </div>
            )}
          </div>

          {/* Payout history */}
          <div>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: C.dark }}>📋 Payout History</h4>
            {(!tutor.payout_history || tutor.payout_history.length === 0) ? (
              <p style={{ color: C.grey, fontSize: 13, margin: 0 }}>No previous payouts recorded.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {tutor.payout_history.slice(0, 5).map((p, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 12px', background: '#F9FAFB', borderRadius: 8, fontSize: 13,
                  }}>
                    <span style={{ color: C.dark, fontWeight: 500 }}>{p.payout_month}</span>
                    <span style={{ color: C.success, fontWeight: 700 }}>${(p.total_net_usd || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending lessons breakdown */}
          {tutor.pending_lessons && tutor.pending_lessons.length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: C.dark }}>📚 Lessons to Pay ({tutor.pending_earnings_count})</h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#F3F4F6' }}>
                      {['Date', 'Student', 'Duration', 'Gross', 'Fee', 'Net'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: C.grey, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tutor.pending_lessons.map((l, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: '8px 12px', color: C.dark }}>{l.lesson_date ? new Date(l.lesson_date).toLocaleDateString('en-GB') : '—'}</td>
                        <td style={{ padding: '8px 12px', color: C.dark }}>{l.student_name || '—'}</td>
                        <td style={{ padding: '8px 12px', color: C.grey }}>{l.duration_minutes || '—'} min</td>
                        <td style={{ padding: '8px 12px', color: C.dark }}>${(l.gross_usd || 0).toFixed(2)}</td>
                        <td style={{ padding: '8px 12px', color: C.error }}>${(l.platform_fee_usd || 0).toFixed(2)}</td>
                        <td style={{ padding: '8px 12px', color: C.success, fontWeight: 600 }}>${(l.net_usd || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#F0F7FA' }}>
                      <td colSpan={3} style={{ padding: '10px 12px', fontWeight: 700, color: C.dark }}>Total</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: C.dark }}>${(tutor.pending_gross_usd || 0).toFixed(2)}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: C.error }}>${(tutor.pending_fees_usd || 0).toFixed(2)}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: C.teal }}>${(tutor.pending_net_usd || 0).toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirm payout modal */}
      {confirming && (
        <div style={{
          borderTop: `2px solid ${C.teal}`, background: C.lightTeal, padding: '20px 24px',
        }}>
          <h4 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.dark }}>
            Confirm Payout — {tutor.first_name} {tutor.last_name}
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.dark, display: 'block', marginBottom: 4 }}>
                Payment Reference <span style={{ color: C.grey, fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                value={payRef}
                onChange={e => setPayRef(e.target.value)}
                placeholder="e.g. Bank transfer ref, PayPal ID…"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: `1px solid ${C.border}`, fontSize: 14, outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.dark, display: 'block', marginBottom: 4 }}>
                Notes <span style={{ color: C.grey, fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                value={payNotes}
                onChange={e => setPayNotes(e.target.value)}
                placeholder="Any notes about this payout…"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: `1px solid ${C.border}`, fontSize: 14, outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div style={{
            background: C.white, borderRadius: 8, padding: '12px 16px', marginBottom: 16,
            border: `1px solid ${C.border}`, fontSize: 13,
          }}>
            This will mark <strong>{tutor.pending_earnings_count} lessons</strong> as paid
            and record a payout of <strong style={{ color: C.teal }}>${(tutor.pending_net_usd || 0).toFixed(2)} net</strong> to {tutor.first_name}.
            An email notification will be sent to <strong>{tutor.email}</strong>.
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onMarkPaid}
              disabled={marking}
              style={{
                padding: '10px 24px', borderRadius: 8, fontWeight: 700, fontSize: 14,
                background: marking ? C.grey : C.success, color: C.white, border: 'none',
                cursor: marking ? 'not-allowed' : 'pointer',
              }}
            >
              {marking ? 'Processing…' : '✓ Confirm & Mark Paid'}
            </button>
            <button
              onClick={onConfirmClose}
              style={{
                padding: '10px 20px', borderRadius: 8, fontWeight: 600, fontSize: 14,
                background: C.white, color: C.dark, border: `1px solid ${C.border}`,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: C.grey, minWidth: 110, paddingTop: 1 }}>{label}:</span>
      <span style={{ fontSize: 13, color: C.dark, fontFamily: mono ? 'monospace' : 'inherit', fontWeight: mono ? 600 : 400 }}>
        {value}
      </span>
    </div>
  );
}
